import React from 'react';
import { classNames } from 'react-extras';
import { Subscription } from 'rxjs';
import { PageContextType } from 'types';
import { CommonPricingTranslations } from '../types';
import { Grid, GridCol, GridRow } from '../../../UI/Grid';
import {
    CountryCurrency,
    DEFAULT_COUNTRY_CURRENCY,
    getCountryCurrency,
    getUserCountry,
} from '../../../../helpers';
import { PlanFrequencyEnum } from '../../../../enum/frequency';
import { PrismicLink } from '../../../PrismicLink';
import { SliceContext } from '../../types';
import { PricingTab, PricingTabDocument } from './PricingTab';
import '../Pricing.scss';

type Tabs = Array<{
    tab: {
        document: PricingTabDocument[];
    };
}>;
export type PricingTableDocument = {
    data: CommonPricingTranslations & {
        tabs: Tabs;
    };
};

export type PricingTableProps = {
    table: PricingTableDocument;
    sliceContext: SliceContext;
    pageContext: PageContextType;
};

export type PricingTableState = {
    activeFrequency: PlanFrequencyEnum;
    countryCurrency: CountryCurrency;
};

const getCurrentTabUid = ({ tabs, pathname }: { tabs: Tabs; pathname: string }) => {
    const indexOfBeforeLastSlash = pathname.lastIndexOf('/', pathname.length - 2);
    const indexOfBeforeLastCharacter = pathname.length - 1;
    let subRoute = pathname.substring(indexOfBeforeLastSlash + 1, indexOfBeforeLastCharacter);
    const selectedTab = tabs.filter(tab => tab.tab.document[0].uid === subRoute);
    return selectedTab[0] ? selectedTab[0].tab.document[0].uid : tabs[0].tab.document[0].uid;
};

const getTabLink = (uid: string, pageContext: PageContextType) => {
    const indexOfPageUID = pageContext.url.indexOf(pageContext.uid);
    const rootPricingPageURL = pageContext.url.substring(
        0,
        indexOfPageUID + pageContext.uid.length + 1,
    );
    const url = `${rootPricingPageURL}${uid}/`;
    return { url };
};

export class PricingTable extends React.Component<PricingTableProps, PricingTableState> {
    geolocation$: Subscription;

    constructor(props: PricingTableProps) {
        super(props);

        this.state = {
            activeFrequency: PlanFrequencyEnum.Yearly,
            countryCurrency: DEFAULT_COUNTRY_CURRENCY,
        };
    }

    componentDidMount() {
        this.geolocation$ = getUserCountry().subscribe((countryCode: string) => {
            this.setState({ countryCurrency: getCountryCurrency(countryCode) });
        });
    }

    componentWillUnmount() {
        this.geolocation$.unsubscribe();
    }

    toggleFrequency = () => {
        const newFrequency =
            this.state.activeFrequency === PlanFrequencyEnum.Yearly
                ? PlanFrequencyEnum.Monthly
                : PlanFrequencyEnum.Yearly;
        this.setState({ activeFrequency: newFrequency });
    };

    renderTabToggle = ({
        tabs,
        sliceContext,
        pageContext,
    }: {
        tabs: Tabs;
        sliceContext: SliceContext;
        pageContext: PageContextType;
    }) => {
        const currentTabUid = getCurrentTabUid({ tabs, pathname: pageContext.url });
        return (
            <div className="PricingTable-tab ButtonSwitch ButtonSwitch--light">
                {tabs.map((item, index) => (
                    <PrismicLink
                        link={getTabLink(item.tab.document[0].uid, pageContext)}
                        key={index}
                        slice={sliceContext}
                    >
                        <button
                            className={classNames('ButtonSwitch-item Button--xs', {
                                'is-selected': currentTabUid === item.tab.document[0].uid,
                            })}
                        >
                            <span className="Button-wrapper">
                                <span className="Button-text">
                                    {item.tab.document[0].data.name}
                                </span>
                            </span>
                        </button>
                    </PrismicLink>
                ))}
            </div>
        );
    };

    renderFrequencyToggle = ({ translations }: { translations: CommonPricingTranslations }) => (
        <div className="PricingTable-frequency Switch">
            <input
                type="checkbox"
                id="frequency-switch"
                checked={this.state.activeFrequency === PlanFrequencyEnum.Yearly}
                onChange={this.toggleFrequency}
            />
            <span className="Switch-text">{translations.toggle_monthly}</span>
            <label htmlFor="frequency-switch" />
            <span className="Switch-text">{translations.toggle_yearly}</span>
        </div>
    );

    renderPricingTab = ({
        tabs,
        translations,
        sliceContext,
        pageContext,
    }: {
        tabs: Tabs;
        translations: CommonPricingTranslations;
        sliceContext: SliceContext;
        pageContext: PageContextType;
    }) => {
        const URLContainsTabUID = tabs.some(tabItem =>
            pageContext.url.includes(tabItem.tab.document[0].uid),
        );
        if (URLContainsTabUID) {
            return tabs.map((item, index) => {
                if (pageContext.url.includes(`/${item.tab.document[0].uid}`)) {
                    return (
                        <PricingTab
                            key={index}
                            tab={item.tab.document[0]}
                            frequency={this.state.activeFrequency}
                            countryCurrency={this.state.countryCurrency}
                            commonTranslations={translations}
                            sliceContext={sliceContext}
                        />
                    );
                }
            });
        }
        /**
         * Return the pricing tab corresponding with the first tab option if no UID is specified in the URL
         * i.e. /en/pricing instead of /en/pricing/release
         */
        return (
            <PricingTab
                tab={tabs[0].tab.document[0]}
                frequency={this.state.activeFrequency}
                countryCurrency={this.state.countryCurrency}
                commonTranslations={translations}
                sliceContext={sliceContext}
            />
        );
    };

    render() {
        const { sliceContext, pageContext } = this.props;
        const { tabs, ...translations } = this.props.table.data;

        return (
            <div className="PricingTable">
                <Grid>
                    <GridRow>
                        <GridCol className="PricingTable-header" colSize={'12'}>
                            {this.renderTabToggle({
                                tabs,
                                sliceContext,
                                pageContext,
                            })}
                            {this.renderFrequencyToggle({ translations })}
                        </GridCol>
                    </GridRow>
                </Grid>

                {this.renderPricingTab({
                    tabs,
                    translations,
                    sliceContext,
                    pageContext,
                })}
            </div>
        );
    }
}
